import {
  baseURLImage,
  getFileNameAndExtension,
  getMimeType,
  sanitizeDocumentUrl,
} from "./utils";

const GOOGLE_VIEWER_EXTENSIONS = [
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".csv",
];

const DIRECT_OPEN_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp"];

/**
 * Opens a document in a new tab for preview.
 * - Images: opened directly.
 * - Office docs: Google Docs viewer (requires a publicly accessible URL).
 * - PDFs and other files: fetched and opened as a blob so the browser renders
 *   them natively. Avoids Google Docs viewer, which fails for many users when
 *   Google's servers cannot fetch the file URL.
 *
 * @param {string} urlOrPath - Full HTTPS URL or storage-relative path
 * @param {{ preferredFileName?: string|null, isFullUrl?: boolean }} [options]
 */
export const openDocumentPreviewInNewTab = async (
  urlOrPath,
  { preferredFileName = null, isFullUrl = false } = {}
) => {
  if (!urlOrPath) return;

  const fullUrl = isFullUrl
    ? sanitizeDocumentUrl(urlOrPath)
    : sanitizeDocumentUrl(baseURLImage(urlOrPath));

  const pathFileName = urlOrPath.split("/").pop() || "";
  let { extension } = getFileNameAndExtension(
    preferredFileName || pathFileName
  );
  if (!extension && pathFileName) {
    extension = getFileNameAndExtension(pathFileName).extension;
  }
  const extLower = (extension || "").toLowerCase();

  if (extLower && DIRECT_OPEN_EXTENSIONS.includes(extLower)) {
    window.open(fullUrl, "_blank", "noopener,noreferrer");
    return;
  }

  if (extLower && GOOGLE_VIEWER_EXTENSIONS.includes(extLower)) {
    window.open(
      `https://docs.google.com/viewer?url=${encodeURIComponent(
        fullUrl
      )}&embedded=true`,
      "_blank",
      "noopener,noreferrer"
    );
    return;
  }

  try {
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(response.statusText || "Failed to load file");
    }
    const blob = await response.blob();
    const mime = extLower ? getMimeType(extLower) : blob.type;
    const typedBlob = new Blob([blob], { type: mime });
    const objectUrl = URL.createObjectURL(typedBlob);
    const previewWin = window.open(objectUrl, "_blank", "noopener,noreferrer");
    if (!previewWin) {
      URL.revokeObjectURL(objectUrl);
      return;
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
  } catch (e) {
    console.error("openDocumentPreviewInNewTab:", e);
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  }
};

/**
 * Opens a stored attachment in a new tab for preview. Fetches the file and
 * re-applies MIME type from the filename so preview works when the object was
 * uploaded with generic S3 metadata (e.g. mobile) that would otherwise trigger
 * a download on direct navigation.
 */
export const openAttachmentPreviewInNewTab = async (
  filePath,
  preferredFileName = null
) =>
  openDocumentPreviewInNewTab(filePath, {
    preferredFileName,
    isFullUrl: false,
  });
